import * as Sentry from '@sentry/browser';

import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { getTrafficCoverageLevelByPercentage } from '@hotjar/webapp/domains/revenue/sampling/utils';
import type { TrafficCoverageLevel } from '@hotjar/webapp/domains/revenue/sampling/types';
import type { Site, DailySessionsData } from '@hotjar/site';
import { between } from '@hotjar/insights-common/lib/between';

import { useCurrentSite } from '@hotjar/site';
import { useDailySession } from './useDailySession';

export interface UseTrafficCoverage {
  isError: boolean;
  isLoading: boolean;
  days: number;
  dailyTraffic: number;
  isCoverageMeasureComplete: boolean;
  coveragePercentage: number;
  ccDailyAllowance: number;
  coverageLevel: TrafficCoverageLevel;
  hasPartialMeasure: boolean;
}

const PARTIAL_MEASURE_AFTER_DAY = 1;
const PARTIAL_MEASURE_BEFORE_DAY = 7;

const isPartialMeasure = (days: number): boolean =>
  between(days, PARTIAL_MEASURE_AFTER_DAY, PARTIAL_MEASURE_BEFORE_DAY);

const useCCDailyAllowance = (site: Site | undefined, overrideCCDailyAllowance?: number) => {
  const planRepository = usePlanRepository();

  const siteCCDailyAllowance = site?.effectivePlanId
    ? planRepository.byId(site.effectivePlanId).ccDailyAllowance
    : 0;

  return overrideCCDailyAllowance ?? siteCCDailyAllowance;
};

const getTrafficCoverageData = (
  dailySessionsData: DailySessionsData | undefined,
  ccDailyAllowance: number,
) => {
  const days = dailySessionsData?.days ?? 0;
  const dailyTraffic = dailySessionsData?.sessions ?? 0;
  const isCoverageMeasureComplete = days >= 7;
  const coveragePercentage =
    Math.min(100, Math.floor((ccDailyAllowance / dailyTraffic) * 100)) || 0;
  const coverageLevel = getTrafficCoverageLevelByPercentage(coveragePercentage);

  return {
    days,
    dailyTraffic,
    isCoverageMeasureComplete,
    coveragePercentage,
    coverageLevel,
    hasPartialMeasure: isPartialMeasure(days),
  };
};

export function useTrafficCoverage(
  overrideSite?: Site,
  overrideCCDailyAllowance?: number,
): UseTrafficCoverage {
  const { data: currentSite, isLoading: isLoadingCurrentSite } = useCurrentSite();
  const site = overrideSite ?? currentSite;
  const {
    data: dailySessionsData,
    isLoading: isLoadingDailySession,
    error,
  } = useDailySession(site);
  const ccDailyAllowance = useCCDailyAllowance(site, overrideCCDailyAllowance);

  if (error) {
    Sentry.captureMessage(`Could not get the daily sessions from site ${site?.id}.`);
  }

  const {
    days,
    dailyTraffic,
    isCoverageMeasureComplete,
    coveragePercentage,
    coverageLevel,
    hasPartialMeasure,
  } = getTrafficCoverageData(dailySessionsData, ccDailyAllowance);

  return {
    isError: Boolean(error),
    isLoading: isLoadingCurrentSite || isLoadingDailySession,
    days,
    dailyTraffic,
    isCoverageMeasureComplete,
    coveragePercentage,
    ccDailyAllowance,
    coverageLevel,
    hasPartialMeasure,
  };
}
